import React, { useEffect } from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

import { sendVerificationEmail, hasCurrentUserErrors, marketplaceCommingSoonRequest, subscriptionPermissionRequest, restrictMarketplcaeRequest, reevaluateElephantVerification, setElephantVerificationModal, setNotificationModalOpen, requestGroupPermissionModal } from '../../ducks/user.duck';
import { logout, authenticationInProgress, saveShopifyDetails } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { deleteBookMark, fetchUserActivity, getNotificationPreference, getNotificationsCount, getNotificationsList, markAllNotificationsAsRead, markNotificationAsRead, resetNotificationCount, successNotifications, toggelRequestSpaceReducer, toggleCreatePost, updateNotificationPreference } from '../CircleCommunityPage/CircleCommunityPage.duck';
import { uploadImage, updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import IntercomComponent from '../../components/IntercomComponent/IntercomComponent';
import { onElephantTrustSignupCheck } from '../../util/api';
import OneSignalComponent from '../../components/OneSignal/OneSignalComponent';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));



export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    elephantVerificationModal,
    onReevaluateElephantVerification,
    onSetElephantVerificationModal,
    elephantVerificationInProgress,
    elephantVerificationError,
    ...rest
  } = props;

  const nonLoggedInUserEmail = "";
  const dispatch = useDispatch();
  return (
    <>
      {process.env.NODE_ENV == 'production' ? <IntercomComponent user={currentUser} nonLoggedInUserEmail={nonLoggedInUserEmail} /> : null}
      {process.env.NODE_ENV == 'production' ? <OneSignalComponent user={currentUser} /> : null}
      <Topbar
        authInProgress={authInProgress}
        currentPage={currentPage}
        currentSearchParams={currentSearchParams}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        history={history}
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        location={location}
        notificationCount={notificationCount}
        onLogout={onLogout}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        showGenericError={hasGenericError}
        elephantVerificationModal={elephantVerificationModal}
        onReevaluateElephantVerification={onReevaluateElephantVerification}
        onSetElephantVerificationModal={onSetElephantVerificationModal}
        elephantVerificationInProgress={elephantVerificationInProgress}
        elephantVerificationError={elephantVerificationError}
        {...rest}
      />
    </>
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    marketplaceCommingSoonRequest,
    restrictMarketplcaeRequest,
    subscriptionPermissionRequest,
    requestGroupPermissionModal,
    isAddAnItemPage,
    currentUserNotificationCountForOffer,
    currentUserOrderNotificationCount,
    currentUserUnreadInquiriesCount,
    elephantVerificationModal,
    elephantVerificationInProgress,
    elephantVerificationError,
    topbarGroups,
    userModeratorsGroups,
    isOpenNotificationModal
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const spaceListData = Array.isArray(topbarGroups) && topbarGroups.length > 0 ? topbarGroups : [];
  const {
    requestNotificationsInProgress,
    notificationsData,
    notificationCountData,
    requestNotificationPreferenceInProgress,
    notificaitonPreferenceData,
    requestBookmarksList,
    bookmarksListData,
    currentCommunityMember,
    fetchUserActivityRequest,
    fetchUserActivityData,
    fetchUserActivityError,
    requestNewSpace,
    isRequestSpace,
    onRequestGroupClick,
  } = state.CircleCommunityPage;

  const notificationCount = currentUserNotificationCount || currentUserNotificationCountForOffer;

  const {
    userFavChatDetails,
    chatDetails,
  } = state.RuleBooksSearchPage;

  // Business Profile Modal state from ProfileSettingsPage
  const {
    image: businessProfileImage,
    uploadImageError: businessProfileUploadImageError,
    uploadInProgress: businessProfileUploadInProgress,
    updateInProgress: businessProfileUpdateInProgress,
    updateProfileError: businessProfileUpdateError,
  } = state.ProfileSettingsPage || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    marketplaceCommingSoonRequest,
    restrictMarketplcaeRequest,
    subscriptionPermissionRequest,
    requestGroupPermissionModal,
    isAddAnItemPage,
    requestNotificationsInProgress,
    notificationsData,
    notificationCountData,
    requestNotificationPreferenceInProgress,
    notificaitonPreferenceData,
    currentUserOrderNotificationCount,
    currentUserUnreadInquiriesCount,
    elephantVerificationModal,
    elephantVerificationInProgress,
    elephantVerificationError,
    spaceListData,
    userModeratorsGroups,
    requestBookmarksList,
    bookmarksListData,
    currentCommunityMember,
    fetchUserActivityRequest,
    fetchUserActivityData,
    fetchUserActivityError,
    requestNewSpace,
    isRequestSpace,
    userFavChatDetails,
    chatDetails,
    isOpenNotificationModalRedux: isOpenNotificationModal,
    // Business Profile Modal props
    businessProfileImage,
    businessProfileUploadImageError,
    businessProfileUploadInProgress,
    businessProfileUpdateInProgress,
    businessProfileUpdateError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onMarketplaceCommingSoonRequest: (type) => dispatch(marketplaceCommingSoonRequest(type)),
  onRestrictMarketplcaeRequest: (type) => dispatch(restrictMarketplcaeRequest(type)),
  onSubscriptionPermissionRequest: (type) => dispatch(subscriptionPermissionRequest(type)),
  onRequestGroupPermissionModal: (type) => dispatch(requestGroupPermissionModal(type)),
  onFetchNotifications: params => dispatch(getNotificationsList(params)),
  onSuccessNotifications: () => dispatch(successNotifications({})),
  onFetchNotificationsCount: params => dispatch(getNotificationsCount(params)),
  onMarkNotificationRead: (id) => dispatch(markNotificationAsRead(id)),
  onResetNotificationsCount: () => dispatch(resetNotificationCount()),
  onMarkAllNotificationsAsRead: () => dispatch(markAllNotificationsAsRead()),
  onGetNotificationPreference: () => dispatch(getNotificationPreference()),
  onUpdateNotificationPreference: (params) => dispatch(updateNotificationPreference(params)),
  onReevaluateElephantVerification: (params) => dispatch(reevaluateElephantVerification(params)),
  onSetElephantVerificationModal: (params) => dispatch(setElephantVerificationModal(params)),
  onSetNotificationModalOpen: (isOpen) => dispatch(setNotificationModalOpen(isOpen)),
  onDeleteBookMark: apiParams => dispatch(deleteBookMark(apiParams)),
  onFetchUserActivity: (params) => dispatch(fetchUserActivity(params)),
  onRequestGroupClick: value => dispatch(toggelRequestSpaceReducer(value)),
  onToggleCreatePost: () => dispatch(toggleCreatePost()),
  // Business Profile Modal actions
  onBusinessProfileImageUpload: params => dispatch(uploadImage(params)),
  onBusinessProfileUpdate: params => dispatch(updateProfile(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
